import React, { useContext, useEffect, useState } from "react";
import { makeStyles } from "@mui/styles";
import {
  ClassExtended,
  InsightsCtx,
  ThemeCtx,
  UniversalModalCtx,
} from "../../../../interfaces";
import { UsersManagementProps } from "../../../../pages/v2/UsersPage";
import { Role, SubRoles, _AuthType } from "../../../../enums";
import {
  Box,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { ReactComponent as IconClass } from "../../../../assets/MACS-UserMangmt-Icon-PreAlgebra.svg";
import { ClassDeletePrompt } from "../utils/ClassDeletePrompt";
import { EditClass } from "../EditDetails/EditClass";
import { ViewClass } from "../ViewDetails/ViewClass";
import { toast } from "react-toastify";
import { ShareAccessModal } from "../ShareAccessModal";
import {
  addObservers,
  deleteObserver,
  FetchClassStudents,
  formatDate,
  generateGtmEvent,
  getObservers,
  ReSendLoginCredentialsToClass,
} from "../../../../Helpers";
import { ThemeContext } from "../../../../context/ThemeContext";
import { ErrorToast, SuccessToast } from "../../utils/Toast";
import { SettingsButton } from "../utils/SettingsButton";
import { SendClassCredentialsModal } from "../utils/SendClassCredentialsModal";
import { styled } from "@mui/material/styles";
import { UniversalModalContext } from "../../../../context/UniversalModalContext";
import { PopUpDialog } from "../../utils/PopUpDialog";
import { GenericDataTable } from "./GenericDataTable";
import { GridColDef } from "@mui/x-data-grid";
import { useHistory } from "react-router-dom";
import { MacsAuthenticator } from "../../../../hooks/useMacsAuthenticator";
import { InsightsContext } from "../../../../context/InsightsContext";

const useStyles = makeStyles((theme) => ({
  root: {
    textAlign: "left",
    display: "flex",
    flexDirection: "column",
  },
  gridRow: {
    padding: "0rem 28px 0rem 0px",
    minHeight: "86px",
    borderTop: "#E8E8E8 1px solid",
    "&:hover": {
      backgroundColor: "#F5F5F5",
    },
    cursor: "pointer",
  },
  rowIcon: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  gridItem: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0, // Prevents the grid item from shrinking
  },

  stackedData: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1, // Allows this box to grow to fill available space
    flexBasis: "auto", // Basis is automatic, adjusts based on content
    overflow: "hidden", // Hides overflowed text
    marginLeft: "28px",
  },
  fieldData: {
    fontWeight: 500,
    fontSize: 18,
    color: theme.palette.text.secondary,
    whiteSpace: "normal", // Allows text to wrap
    overflow: "hidden",
    textOverflow: "ellipsis", // Adds ellipsis for overflowed text
    maxWidth: "100%", // Ensures the text does not exceed its container
  },
  fieldNumberData: {
    fontWeight: 500,
    fontSize: 18,
    color: theme.palette.text.secondary,
    textAlign: "left",
  },
  fieldName: {
    fontSize: 14,
    fontWeight: 400,
    color: theme.palette.text.secondary,
  },
  editColumn: {
    display: "flex",
    alignItems: "center",
  },
  viewButton: {
    color: theme.palette.text.secondary,
    "&:hover": {
      backgroundColor: "#F5F5F5",
    },
  },
  deleteUserButton: {
    borderColor: "#8C1D40",
    backgroundColor: "#8C1D40",
    color: "#FFFFFF",
    fontSize: "1rem",
    fontWeight: 600,
    borderRadius: "2.25rem",
    paddingLeft: "1rem",
    paddingRight: "1rem",
    paddingTop: "0.5rem",
    paddingBottom: "0.5rem",
    borderStyle: "solid",
    borderWidth: "1px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",

    "&:hover": {
      backgroundColor: "#701733",
    },
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: 8,
  },
}));

interface ClassesTableProps extends UsersManagementProps {
  variant?: "simple" | "table";
}

export const ClassesTable: React.FunctionComponent<ClassesTableProps> = (props) => {
  const { variant = "simple" } = props;
  const history = useHistory();
  const classes = useStyles();
  const themeCtx = useContext(ThemeContext) as ThemeCtx;
  const insightsCtx = useContext(InsightsContext) as InsightsCtx;
  const universalModalCtx = useContext(UniversalModalContext) as UniversalModalCtx;

  const [promptOpen, setPromptOpen] = useState(false);
  const [deleteClass, setDeleteClass] = useState<ClassExtended | undefined>();
  const [viewOpen, setViewOpen] = useState(false);
  const [viewClass, setViewClass] = useState<ClassExtended | undefined>();
  const [editOpen, setEditOpen] = useState(false);
  const [editClass, setEditClass] = useState<ClassExtended | undefined>();
  const [shareOpen, setShareOpen] = useState(false);
  const [shareClass, setShareClass] = useState<ClassExtended | undefined>();
  const [observerList, setObserverList] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loginCredentialsPromptOpen, setLoginCredentialsPromptOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState<ClassExtended | undefined>();
  const [students, setStudents] = useState<any[]>([]);
  const [failedToSendToStudents, setFailedToSendToStudents] = useState<any[]>([]);
  const [allUserActivatedModal, setAllUserActivatedModal] = useState(false);
  let isSuperOrAdmin = props.currentUser?.role !== Role.teacher;
  let isSuper = props.currentUser?.role === Role.super;

  const [sendLoginCredentialsError, setSendLoginCredentialsError] = useState<
    undefined | string
  >(undefined);

  const handlePromptClose = () => {
    setPromptOpen(false);
  };

  const handleEditClose = () => {
    setEditOpen(false);
  };

  const handleViewClose = () => {
    setViewOpen(false);
  };

  const handleDeleteClick = (classe: ClassExtended | undefined) => {
    setDeleteClass(classe);
    setPromptOpen(true);
  };

  const handleEditClick = (classe: ClassExtended | undefined) => {
    setEditClass(classe);
    setViewOpen(false);
    setEditOpen(true);
  };

  const handleViewClick = (classe: ClassExtended | undefined) => {
    setViewClass(classe);
    setViewOpen(true);
  };

  const handleGoToClass = (classe: ClassExtended | undefined) => {
    if (!classe) return;
    history.push(insightsCtx.getClassUrl(classe.id));
  };

  const handleShareClick = (classe: ClassExtended | undefined) => {
    setShareClass(classe);
    setShareOpen(true);
  };

  useEffect(() => {
    const getClassObserver = async (shareClass: any) => {
      try {
        let result = await getObservers(`classId=${shareClass?.id}`);
        setObserverList([
          // ...[
          //   {
          //     id: shareClass.teacher_id,
          //     full_name: shareClass.teacher_name,
          //     email: shareClass.teacher_email,
          //     role: "Teacher",
          //   },
          // ],
          ...result.teachers,
          ...result.observers,
        ]);
        setIsLoading(false);
      } catch (e: any) {
        let errorMessages = e.message as string;
        toast.error(errorMessages);
        setIsLoading(false);
      }
    };
    if (shareOpen && shareClass) {
      setIsLoading(true);
      getClassObserver(shareClass);
    }
  }, [shareOpen, shareClass]);

  const emptySpace = <span>&nbsp;</span>;

  const renderObserverEye = (classe: ClassExtended) => {
    if (classe.observer_ids?.includes(props.currentUser.id)) {
      let toolTipLabel = "";
      if (classe.class_observer_ids?.includes(props.currentUser.id)) {
        toolTipLabel = "You're observing this class";
      } else if (classe.student_observer_ids?.includes(props.currentUser.id)) {
        toolTipLabel = "You're observing students in this class";
      }

      return (
        <Tooltip title={toolTipLabel} placement="bottom" arrow>
          <i
            className="far fa-eye fa-lg"
            style={{ color: themeCtx.theme.palette.text.secondary }}></i>
        </Tooltip>
      );
    } else {
      return <></>;
    }
  };

  const handleSendClassLoginCredentials = async (classe: ClassExtended) => {
    setSelectedClass(classe);
    FetchClassStudents(classe?.id!).then((res) => {
      let students = res.filter((student: any) => student.last_access_at === null);
      setStudents(students);
      if (students.length > 0) setLoginCredentialsPromptOpen(true);
      else setAllUserActivatedModal(true);
    });
  };

  const renderCellDate = (params: any) => {
    return (
      <div
        style={{
          fontWeight: props.sort?.field === params.field ? 500 : "inherit",
          color:
            props.sort?.field === params.field
              ? themeCtx.theme.palette.primary.main
              : "inherit",
        }}>
        {params.value
          ? new Date(params.value).toLocaleDateString("en-US", {
              timeZone: "UTC",
            })
          : ""}
      </div>
    );
  };

  const renderCellCustom = (params: any, value: string) => {
    return (
      <div
        style={{
          fontWeight: props.sort?.field === params.field ? 500 : "inherit",
          color:
            props.sort?.field === params.field
              ? themeCtx.theme.palette.primary.main
              : "inherit",
        }}>
        {value}
      </div>
    );
  };

  const showObserverDetails = props.classes?.find((item) =>
    item.observer_ids ? true : false
  )
    ? true
    : false;

  return (
    <>
      <div className={classes.root}>
        <Box sx={{ width: "100%" }}>
          {variant === "simple" ? (
            <>
              {props.classes?.map((classe: ClassExtended) => {
                const isReadOnlyAdmin = props.currentUser?.user_subrole?.some(
                  (role) => role.sub_role === SubRoles.ADMIN_READ_ONLY
                );
                const actions = [
                  {
                    label: "Edit",
                    color: "#747474",
                    onClick: () => handleEditClick(classe),
                    visible: !isReadOnlyAdmin,
                    isDisabled: false,
                  },
                  {
                    label: "View",
                    color: "#747474",
                    onClick: () => handleViewClick(classe),
                    visible: true,
                    isDisabled: false,
                  },
                  {
                    label: "Delete",
                    color: "#CC2F2F",
                    onClick: () => handleDeleteClick(classe),
                    visible: false,
                    isDisabled: false,
                  },
                  {
                    label: "Share",
                    color: "#747474",
                    onClick: () => handleShareClick(classe),
                    isDisabled: classe.observer_ids?.includes(props.currentUser.id),
                    visible: !isReadOnlyAdmin,
                  },
                  {
                    label: "Send login credentials",
                    color: "#747474",
                    onClick: () => handleSendClassLoginCredentials(classe),
                    isDisabled: false,
                    visible:
                      !isReadOnlyAdmin &&
                      MacsAuthenticator.authHook.authType !== _AuthType.LTI,
                  },
                ];

                return (
                  <Grid
                    container
                    sx={{ width: "100%" }}
                    key={classe.id}
                    className={classes.gridRow}>
                    <Grid
                      onClick={() => {
                        handleViewClick(classe);
                      }}
                      item
                      xs={isSuper ? 5 : 6}
                      className={classes.gridItem}>
                      <Box
                        sx={{
                          width: "50px",
                          minWidth: "50px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}>
                        {renderObserverEye(classe)}
                      </Box>
                      <Box className={classes.rowIcon}>
                        <IconClass style={{ width: 48, height: 48 }} />
                      </Box>
                      <Box
                        className={classes.stackedData}
                        sx={{
                          marginLeft: "28px",
                        }}>
                        <Typography className={classes.fieldData}>
                          {`${classe.course_name} ${classe.period_block}`}
                        </Typography>
                        <Typography className={classes.fieldName}>
                          {`Start date: ${
                            classe.start_date && formatDate(new Date(classe.start_date))
                          }`}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid
                      onClick={() => {
                        handleViewClick(classe);
                      }}
                      item
                      xs={isSuper ? 2 : 3}
                      className={classes.gridItem}>
                      <Box className={classes.stackedData}>
                        {isSuperOrAdmin && (
                          <>
                            <Typography className={classes.fieldData}>
                              {classe.teacher_name ?? emptySpace}
                            </Typography>
                            <Typography className={classes.fieldName}>Teacher</Typography>
                          </>
                        )}
                      </Box>
                    </Grid>
                    {isSuper && (
                      <Grid
                        onClick={() => {
                          handleViewClick(classe);
                        }}
                        item
                        xs={2}
                        className={classes.gridItem}>
                        <Box className={classes.stackedData}>
                          <>
                            <Typography className={classes.fieldData}>
                              {classe.school_name ?? emptySpace}
                            </Typography>
                            <Typography className={classes.fieldName}>School</Typography>
                          </>
                        </Box>
                      </Grid>
                    )}
                    <Grid
                      onClick={() => {
                        handleViewClick(classe);
                      }}
                      item
                      xs={2}
                      className={classes.gridItem}>
                      <Box className={classes.stackedData}>
                        <>
                          <Typography className={classes.fieldNumberData}>
                            {classe.student_count ?? emptySpace}
                          </Typography>
                          <Typography className={classes.fieldName}>
                            Number of students
                          </Typography>
                        </>
                      </Box>
                    </Grid>
                    <Grid item xs={1} className={classes.gridItem}>
                      <Box sx={{ marginLeft: "auto" }}>
                        <SettingsButton
                          label={!isSuperOrAdmin ? "" : "Actions"}
                          sectionName={`${classe.course_name} ${classe.period_block}`}
                          actions={actions}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                );
              })}
            </>
          ) : (
            <GenericDataTable<ClassExtended & { action?: any; class_name?: any }>
              data={props.classes ?? []}
              currentUser={props.currentUser}
              onDelete={props.onDelete!}
              sort={props.sort!}
              pagination={props.pagination}
              columnNames={
                showObserverDetails
                  ? [
                      "observer_ids",
                      "class_name",
                      "course_name",
                      "teacher_name",
                      "period_block",
                      "start_date",
                      "end_date",
                      "action",
                    ]
                  : [
                      "class_name",
                      "course_name",
                      "period_block",
                      "start_date",
                      "end_date",
                      "action",
                    ]
              }
              fieldMap={
                new Map<
                  keyof ClassExtended | "action" | "class_name",
                  GridColDef<ClassExtended>
                >([
                  [
                    "observer_ids",
                    {
                      field: "observer_ids",
                      headerName: "",
                      maxWidth: 20,
                      headerAlign: "center",
                      align: "center",
                      sortable: false,
                      renderCell: (params: any) => {
                        const classe = params.row;
                        return renderObserverEye(classe);
                      },
                    },
                  ],
                  [
                    "class_name",
                    {
                      field: "subject",
                      headerName: "Class name",
                      flex: 1,
                      sortable: true,
                      renderCell: (params: any) =>
                        renderCellCustom(
                          params,
                          `${params.row.subject} ${params.row.period_block}`
                        ),
                    },
                  ],
                  [
                    "course_name",
                    {
                      field: "course_name",
                      headerName: "Course",
                      flex: 1,
                      sortable: true,
                    },
                  ],
                  [
                    "teacher_name",
                    {
                      field: "teacher_name",
                      headerName: "Teacher",
                      flex: 1,
                      sortable: true,
                    },
                  ],
                  [
                    "period_block",
                    {
                      field: "period_block",
                      headerName: "Period/block",
                      flex: 1,
                      sortable: true,
                    },
                  ],
                  [
                    "start_date",
                    {
                      field: "start_date",
                      headerName: "Start Date",
                      flex: 0,
                      sortable: true,
                      renderCell: renderCellDate,
                    },
                  ],
                  [
                    "end_date",
                    {
                      field: "end_date",
                      headerName: "End Date",
                      flex: 0,
                      sortable: true,
                      renderCell: renderCellDate,
                    },
                  ],
                  [
                    "action",
                    {
                      field: "action",
                      headerName: "Actions",
                      headerAlign: "right",
                      align: "right",
                      minWidth: 55,
                      sortable: false,
                      renderCell: (params) => {
                        const classe = params.row;
                        const actions = [
                          ...(props.currentUser.role !== Role.teacher
                            ? [
                                {
                                  label: "Edit",
                                  color: "#747474",
                                  onClick: () => handleEditClick(classe),
                                  visible: true,
                                  isDisabled: false,
                                },
                              ]
                            : []),
                          {
                            label: "View",
                            color: "#747474",
                            onClick: () => handleGoToClass(classe),
                            visible: true,
                            isDisabled: false,
                          },
                          ...(props.currentUser.role !== Role.teacher
                            ? [
                                {
                                  label: "Delete",
                                  color: "#CC2F2F",
                                  onClick: () => handleDeleteClick(classe),
                                  visible: false,
                                  isDisabled: false,
                                },
                              ]
                            : []),
                          {
                            label: "Share",
                            color: "#747474",
                            onClick: () => handleShareClick(classe),
                            isDisabled: classe.observer_ids?.includes(
                              props.currentUser.id
                            ),
                            visible: true,
                          },
                          ...(props.currentUser.role !== Role.teacher
                            ? [
                                {
                                  label: "Send login credentials",
                                  color: "#747474",
                                  onClick: () => handleSendClassLoginCredentials(classe),
                                  isDisabled: false,
                                  visible: true,
                                },
                              ]
                            : []),
                        ];

                        return (
                          <SettingsButton
                            sectionName={`${classe.course_name} ${classe.period_block}`}
                            actions={actions}
                          />
                        );
                      },
                    },
                  ],
                ])
              }
            />
          )}
        </Box>
        <ClassDeletePrompt
          promptOpen={promptOpen}
          promptOnClose={handlePromptClose}
          currentUser={props.currentUser}
          classe={deleteClass}
          onDelete={props.onDelete}
        />
        <ViewClass
          open={viewOpen}
          onClose={handleViewClose}
          onEdit={() => handleEditClick(viewClass)}
          currentUser={props.currentUser}
          classe={viewClass}
        />
        <EditClass
          open={editOpen}
          onClose={handleEditClose}
          onSubmit={props.onDelete}
          currentUser={props.currentUser}
          class={editClass}
        />
        <ShareAccessModal
          loading={isLoading}
          schoolId={shareClass?.school_id!}
          open={shareOpen}
          handleClose={() => {
            setShareOpen(false);
          }}
          assignAccess={async (observerList) => {
            if (observerList.length > 0) {
              // await api call and send toast message
              try {
                const response = await addObservers(
                  {
                    classId: shareClass?.id,
                    type: "OBSERVER_CLASS",
                  },
                  observerList
                );
                if (response.some((res) => res.status === "fulfilled")) {
                  SuccessToast("Observer added successfully!");
                }
                if (response.some((res) => res.status === "rejected")) {
                  response.forEach((res) => {
                    if (res.status === "rejected") {
                      ErrorToast(res.reason);
                    }
                  });
                }
              } catch (e) {
                ErrorToast("Error adding observers");
              }
            }
            setObserverList([]);
          }}
          currentUser={props.currentUser}
          existingObservers={observerList}
          title={`${shareClass?.course_name} ${shareClass?.period_block}  ${
            shareClass?.start_date &&
            new Date(shareClass.start_date).toLocaleDateString("en-US", {
              timeZone: "UTC",
            })
          }`}
          accessFor={"class"}
          removeAccess={async (observerId) => {
            try {
              await deleteObserver({
                type: "OBSERVER_CLASS",
                classId: shareClass?.id,
                grantToId: observerId,
              });
              let newObserverList = observerList.filter(
                (observer) => observer.id !== observerId
              );
              setObserverList([...newObserverList]);
            } catch (e) {
              console.log(e);
            }
          }}
        />
      </div>
      <PopUpDialog
        ctaFooterVariant={"default"}
        promptOpen={allUserActivatedModal}
        handleClose={() => {
          setAllUserActivatedModal(false);
          generateGtmEvent({
            event: "modal",
            action: "close",
            name: "onclick",
            type: "click",
            region: "main content",
            section: "send login credentials",
            text: "close cross",
          });
        }}
        handleOkClick={async () => {
          setAllUserActivatedModal(false);
          generateGtmEvent({
            event: "link",
            action: "click",
            name: "onclick",
            type: "internal link",
            region: "main content",
            section: "send login credentials",
            text: "Ok",
          });
        }}
        handleCancelClick={() => {
          setAllUserActivatedModal(false);
          generateGtmEvent({
            event: "link",
            action: "click",
            name: "onclick",
            type: "internal link",
            region: "main content",
            section: "send login credentials",
            text: "cancel",
          });
        }}
        okButtonLabel={"Ok"}
        popupTitle={`Send login credentials`}
        cancelButtonLabel={undefined}>
        <Stack direction={"column"} spacing={4}>
          <>
            <Typography variant="subtitle2">
              Students in {selectedClass?.course_name} {selectedClass?.period_block}{" "}
              {selectedClass?.start_date &&
                formatDate(new Date(selectedClass.start_date))}{" "}
              have already activated their accounts successfully.
            </Typography>
            <Typography variant={"body1"}>
              Please have them reset their password from the login page if they are having
              trouble logging in, or contact support if you need additional assistance.
            </Typography>
          </>
        </Stack>
      </PopUpDialog>
      <SendClassCredentialsModal
        ctaFooterVariant={"default"}
        promptOpen={loginCredentialsPromptOpen}
        handleClose={() => {
          setLoginCredentialsPromptOpen(false);
          setSendLoginCredentialsError(undefined);
          setStudents([]);
          generateGtmEvent({
            event: "modal",
            action: "close",
            name: "onclick",
            type: "click",
            region: "main content",
            section: sendLoginCredentialsError
              ? "Error sending login credentials"
              : "send login credentials",
            text: "close cross",
          });
        }}
        handleOkClick={async () => {
          setLoginCredentialsPromptOpen(false);
          setSendLoginCredentialsError(undefined);
          setStudents([]);
          if (!sendLoginCredentialsError) {
            universalModalCtx.setLoaderDialogOpen(true);
            universalModalCtx.setLoaderDialogText("Sending login credentials...");
            ReSendLoginCredentialsToClass(selectedClass?.id!)
              .then((response) => {
                universalModalCtx.setLoaderDialogOpen(false);
                universalModalCtx.setLoaderDialogText("");
                if (response.status) {
                  SuccessToast("Login credentials sent successfully!");
                } else {
                  setLoginCredentialsPromptOpen(true);
                  setSendLoginCredentialsError("Error");
                  setFailedToSendToStudents(response.failedUsers);
                }
              })
              .catch((e) => {
                universalModalCtx.setLoaderDialogOpen(false);
                universalModalCtx.setLoaderDialogText("");
                console.log(e.message);
                ErrorToast(e.message);
              });
          }
          generateGtmEvent({
            event: "link",
            action: "click",
            name: "onclick",
            type: "internal link",
            region: "main content",
            section: sendLoginCredentialsError
              ? "Error sending login credentials"
              : "send login credentials",
            text: sendLoginCredentialsError ? "Ok" : "Send",
          });
        }}
        handleCancelClick={() => {
          setLoginCredentialsPromptOpen(false);
          setSendLoginCredentialsError(undefined);
          setStudents([]);
          generateGtmEvent({
            event: "link",
            action: "click",
            name: "onclick",
            type: "internal link",
            region: "main content",
            section: "send login credentials",
            text: "cancel",
          });
        }}
        okButtonLabel={
          sendLoginCredentialsError ? "Ok" : students.length > 0 ? "Send" : "Ok"
        }
        title={
          sendLoginCredentialsError
            ? `Error sending login credentials`
            : `Send login credentials`
        }
        cancelButtonLabel={!sendLoginCredentialsError ? "Cancel" : undefined}>
        <Stack direction={"column"} spacing={4} sx={{ padding: "0px 8px" }}>
          {!sendLoginCredentialsError ? (
            <>
              <Typography variant="body1">
                Confirm you would like to send a welcome email with a temporary password
                to{" "}
                <b>
                  {" "}
                  {selectedClass?.course_name} {selectedClass?.period_block}{" "}
                  {selectedClass?.start_date &&
                    formatDate(new Date(selectedClass.start_date))}{" "}
                </b>
                students who haven't activated their accounts.
              </Typography>
              <Divider />
              <Typography variant={"body1"}>
                The following students will receive the email:
              </Typography>
            </>
          ) : (
            <Typography variant="body1">
              There was an error sending the welcome email to the following students.
              Please try again later or contact digitsupport@asu.edu for assistance.
            </Typography>
          )}
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 700 }} aria-label="customized table">
              <TableBody>
                {!sendLoginCredentialsError
                  ? students.map((row) => {
                      if (row.last_access_at === null) {
                        return (
                          <StyledTableRow key={row.id}>
                            <StyledTableCell component="th" scope="row">
                              {row.full_name}
                            </StyledTableCell>
                          </StyledTableRow>
                        );
                      } else {
                        return <></>;
                      }
                    })
                  : failedToSendToStudents.map((row) => {
                      return (
                        <StyledTableRow key={row.id}>
                          <StyledTableCell component="th" scope="row">
                            {row.full_name}
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </SendClassCredentialsModal>
    </>
  );
};
